// Prompt: "Does this citation support the claim?" check (PRD §4b, §13).
// Sampled subset — Sprint 7 wires the prompt + parser; the runner integration
// awaits Crossref abstract coverage and lands in a follow-up sprint.

import type { ChatRequest } from '../types';

export const PROMPT_VERSION = 'claim_support-1.0';

const VERDICTS = ['supports', 'partially_supports', 'does_not_support', 'cannot_determine'] as const;

const SCHEMA = {
  type: 'object',
  additionalProperties: false,
  required: ['verdict', 'rationale'],
  properties: {
    verdict: { type: 'string', enum: VERDICTS },
    rationale: { type: 'string', minLength: 1, maxLength: 500 },
  },
} as const;

export interface ClaimSupportOutput {
  verdict: (typeof VERDICTS)[number];
  rationale: string;
}

export interface ClaimSupportArgs {
  /** Sentence in the manuscript making the claim, with the citation marker. */
  manuscriptSentence: string;
  /** The cited work's title + abstract (from Crossref/OpenAlex). */
  citedTitle: string;
  citedAbstract: string;
}

export function buildPrompt(args: ClaimSupportArgs): ChatRequest {
  const systemPrompt = `You are an editorial assistant checking whether a citation supports the claim it's attached to. Given the manuscript's claim sentence and the cited paper's abstract, judge: does the abstract substantiate the claim? Be conservative — "cannot_determine" is appropriate when the abstract doesn't cover the topic in enough detail.`;

  const userPrompt = `Manuscript claim sentence:\n${args.manuscriptSentence}\n\nCited paper title: ${args.citedTitle}\nCited paper abstract:\n${args.citedAbstract}\n\nReturn JSON: { "verdict": "supports"|"partially_supports"|"does_not_support"|"cannot_determine", "rationale": <one or two sentences> }`;

  return {
    systemPrompt,
    userPrompt,
    schema: SCHEMA,
    maxTokens: 350,
    promptVersion: PROMPT_VERSION,
  };
}

export function parseResponse(json: unknown): ClaimSupportOutput | null {
  if (typeof json !== 'object' || json === null) return null;
  const o = json as Record<string, unknown>;
  if (
    typeof o.verdict !== 'string' ||
    !VERDICTS.includes(o.verdict as (typeof VERDICTS)[number]) ||
    typeof o.rationale !== 'string'
  ) {
    return null;
  }
  return { verdict: o.verdict as ClaimSupportOutput['verdict'], rationale: o.rationale };
}
